import React, { useCallback, useState } from 'react';
import { FaChevronDown, FaChevronRight, FaFile, FaFilePdf, FaFolder, FaFolderOpen } from 'react-icons/fa';
import type { U3500DocumentTreeNode } from '../../../../../u3500GedDocumentSearch/U3500Service';

export interface U3121ProjectDocumentTreeProps {
  nodes: U3500DocumentTreeNode[];
  selectedPath: string | null;
  onSelectFile: (node: U3500DocumentTreeNode) => void;
}

interface TreeNodeRowProps {
  node: U3500DocumentTreeNode;
  depth: number;
  selectedPath: string | null;
  onSelectFile: (node: U3500DocumentTreeNode) => void;
}

const TreeNodeRow: React.FC<TreeNodeRowProps> = ({ node, depth, selectedPath, onSelectFile }) => {
  const isFolder = node.kind === 'folder';
  const [expanded, setExpanded] = useState(depth < 2);
  const isSelected = selectedPath === node.relativePath;
  const children = node.children ?? [];

  const handleClick = useCallback(() => {
    if (isFolder) {
      setExpanded((v) => !v);
      return;
    }
    onSelectFile(node);
  }, [isFolder, node, onSelectFile]);

  const FileIcon = node.previewable ? FaFilePdf : FaFile;
  const FolderIcon = expanded ? FaFolderOpen : FaFolder;

  return (
    <div>
      <button
        type="button"
        className={`flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-sm hover:bg-gray-100 ${
          isSelected ? 'bg-blue-50 text-blue-800' : 'text-gray-800'
        }`}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
        onClick={handleClick}
      >
        {isFolder ? (
          <span className="inline-flex w-4 shrink-0 justify-center text-gray-500">
            {expanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
          </span>
        ) : (
          <span className="inline-block w-4 shrink-0" />
        )}
        <span className="inline-flex shrink-0 text-amber-600">
          {isFolder ? <FolderIcon size={14} /> : <FileIcon size={14} className={node.previewable ? 'text-red-600' : 'text-gray-500'} />}
        </span>
        <span className="truncate">{node.name}</span>
      </button>
      {isFolder && expanded && children.length > 0 && (
        <div>
          {children.map((child) => (
            <TreeNodeRow
              key={child.relativePath}
              node={child}
              depth={depth + 1}
              selectedPath={selectedPath}
              onSelectFile={onSelectFile}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const U3121ProjectDocumentTree: React.FC<U3121ProjectDocumentTreeProps> = ({
  nodes,
  selectedPath,
  onSelectFile,
}) => {
  return (
    <div className="overflow-auto text-sm" role="tree">
      {nodes.map((node) => (
        <TreeNodeRow
          key={node.relativePath}
          node={node}
          depth={0}
          selectedPath={selectedPath}
          onSelectFile={onSelectFile}
        />
      ))}
    </div>
  );
};

export default U3121ProjectDocumentTree;
